import { api } from "./api";
import type {
  FiyatOnerisi,
  Siparis,
  SiparisAdresi,
  SiparisDurumGuncelleme,
  SiparisIptalGirdisi,
  SiparisKuyrukDurumu,
  SiparisSayfasi,
  UrunFormu,
  YonetimSiparisDetayi,
  YonetimSiparisi,
  YonetimUrunu,
} from "./magaza-tipleri";

/**
 * MAĞAZA UÇLARI — yönetim + koç (dalga 2 / 7a). İzin, kapsam ve sudo SUNUCUDA.
 * Yollar ve gövdeler `apps/api/src/modules/oyun/magaza.controller.ts` ile birebir.
 */

// --- Ürün yönetimi (`oyun.urun.yonet`) ------------------------------------------------------------

export function yonetimUrunleri(): Promise<YonetimUrunu[]> {
  return api("/oyun/yonetim/magaza/urunler");
}

export function urunOlustur(govde: UrunFormu): Promise<YonetimUrunu> {
  return api("/oyun/yonetim/magaza/urunler", { yontem: "POST", govde });
}

export function urunGuncelle(id: string, govde: Partial<UrunFormu>): Promise<YonetimUrunu> {
  return api(`/oyun/yonetim/magaza/urunler/${encodeURIComponent(id)}`, { yontem: "PATCH", govde });
}

export function urunArsivle(id: string): Promise<YonetimUrunu> {
  return api(`/oyun/yonetim/magaza/urunler/${encodeURIComponent(id)}/arsivle`, { yontem: "POST" });
}

/** "X ayda vermek istiyorum" → önerilen puan. Fiyatı Emre girer; sistem yalnız ÖNERİR. */
export function fiyatOnerisi(ay: number, maliyetTl?: string): Promise<FiyatOnerisi> {
  const q = new URLSearchParams({ ay: String(ay) });
  // TL metin ("120.00"): sözleşme `tlSemasi`. Ürün + kargo toplamı olarak girilir (S2).
  if (maliyetTl !== undefined) q.set("maliyetTl", maliyetTl);
  return api(`/oyun/yonetim/magaza/fiyat-onerisi?${q.toString()}`);
}

// --- Sipariş kuyruğu (`oyun.siparis.yonet`) -------------------------------------------------------

/**
 * Sipariş kuyruğu sayfası (50, en yeni önce). Sunucu `{ satirlar, sonraki }` döndürür; `sonraki` doluysa liste
 * KESİLİYOR demektir ve ekran "daha fazla yükle" gösterir (Codex 7A-04 — imleç atılırsa eski iş görünmez kalır).
 */
export function yonetimSiparisleri(
  durum: SiparisKuyrukDurumu,
  imlec?: string,
): Promise<SiparisSayfasi> {
  const q = new URLSearchParams();
  if (durum !== "TUMU") q.set("durum", durum);
  if (imlec) q.set("imlec", imlec);
  const metin = q.toString();
  return api(`/oyun/yonetim/siparisler${metin ? `?${metin}` : ""}`);
}

export function yonetimSiparisi(id: string): Promise<YonetimSiparisDetayi> {
  return api(`/oyun/yonetim/siparisler/${encodeURIComponent(id)}`);
}

export function siparisDurumuGuncelle(
  id: string,
  govde: SiparisDurumGuncelleme,
): Promise<YonetimSiparisi> {
  return api(`/oyun/yonetim/siparisler/${encodeURIComponent(id)}/durum`, {
    yontem: "POST",
    govde,
  });
}

/** İptal puanı iade eder ve stoğu geri açar (aynı işlemde, sunucu). */
export function yonetimSiparisIptal(
  id: string,
  govde: SiparisIptalGirdisi,
): Promise<YonetimSiparisi> {
  return api(`/oyun/yonetim/siparisler/${encodeURIComponent(id)}/iptal`, {
    yontem: "POST",
    govde,
  });
}

/**
 * KÂĞIT VELİ ONAYI (Emre S11): imzalı onayın taraması + adres yüklenir, sipariş onaylanır. İzin
 * `oyun.siparis.kagit_onayla` (sudo; `kvkk.riza.kagit_kaydet` kalıbı); tarama teslim/iptalden 6 ay sonra silinir.
 * Çok parçalı form: `belge` (dosya), `adres` (JSON), isteğe bağlı `veliUserId` — sunucu `kagit-onay` ucu.
 */
export function siparisKagitOnayi(
  id: string,
  girdi: { tarama: File; adres: SiparisAdresi },
): Promise<Siparis> {
  const govde = new FormData();
  govde.set("belge", girdi.tarama);
  govde.set("adres", JSON.stringify(girdi.adres));
  return api(`/oyun/yonetim/siparisler/${encodeURIComponent(id)}/kagit-onay`, {
    yontem: "POST",
    govde,
  });
}

// --- Koç (`oyun.siparis.goruntule`, ASSIGNED) -----------------------------------------------------

/** Salt okur: koç öğrencisinin siparişlerini görür, adres DÖNMEZ. */
export function ogrenciSiparisleri(ogrenciId: string): Promise<Siparis[]> {
  return api(`/oyun/ogrenci/${encodeURIComponent(ogrenciId)}/siparisler`);
}

/**
 * Koç Ödül Merkezi (sürüm 9a, Emre kararı 07.10.2026): kapsamdaki öğrencilerin siparişleri — SALT OKUR.
 * İzin `oyun.siparis.goruntule`; sunucu atanmış öğrencilerle sınırlar, adres DÖNMEZ, yazma ucu yok.
 */
export function kocSiparisleri(imlec?: string): Promise<SiparisSayfasi> {
  return api(`/oyun/koc/siparisler${imlec ? `?imlec=${encodeURIComponent(imlec)}` : ""}`);
}

/** Koç: kapsamdaki öğrencilerin davet kodları ve adayların durumu (`oyun.hareket.goruntule`, salt okur). */
export interface KocDavetSatiri {
  davetId: string;
  kod: string;
  ogrenciId: string | null;
  ogrenciAdi: string;
  olusturma: string;
  arsivli: boolean;
  adaylar: { adayId: string; durum: string }[];
}

export function kocDavetleri(
  imlec?: string,
): Promise<{ satirlar: KocDavetSatiri[]; sonraki: string | null }> {
  return api(`/oyun/yonetim/davetler${imlec ? `?imlec=${encodeURIComponent(imlec)}` : ""}`);
}
